/**
 * Record-payment — the IMPURE Firestore seam (money-module SLICE 2).
 *
 * Three operations, all thin wrappers over the PURE `recordPayment.ts`
 * decisions + the deployed `markPaidManual` callable:
 *   1. `loadPriorPayments`  — read the `payments/` ledger for a project so
 *      the running-balance math is exact.
 *   2. `submitPayment`      — the single write path. A CLEARING payment goes
 *      through `markPaidManual` (admin SDK: clears the gate, writes the
 *      immutable ledger entry, stops the reminder — the SOLE authority). A
 *      PARTIAL payment writes a ledger-only `payments/` doc (gate stays open).
 *   3. `stampProjectMoney`  — set `currency` / `firmId` / `valueINR` on a
 *      project doc where missing, so the foundation's defensive defaults
 *      become real per-project values.
 *
 * The gate is NEVER flipped here directly — clearing is `markPaidManual`'s
 * job (server-authoritative). This module only invokes it / writes a partial
 * ledger row. (Locked invariant: ONE money authority.)
 */
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './firebase';
import { markPaidManual } from './gateOverride';
import type { Tier } from './engagementTemplate';
import type { FeeHistoryEntry } from './projectStore';
import {
  buildAgreedPriceUpdate,
  buildMarkPaidCall,
  partialPaymentBody,
  planPayment,
  settlementFields,
  settlementOf,
  type PayContextProject,
  type PayableGate,
  type SettlementFx,
  type UiMethod,
  type PaymentPlan,
  type PriorPayment,
} from './recordPayment';
import { firmFor, type Currency, type FirmId } from './firms';

/**
 * Read every prior `payments/` ledger row for a project, narrowed to the
 * running-balance math. Mirrors the CF's `where('projectId','==',…)` query
 * (the same field the CF stamps). A doc with no `partial` flag predates this
 * slice (or is a CF gate-clearing payment) → treated as non-partial (it
 * cleared its gate already, so it does not add to an open balance).
 */
export async function loadPriorPayments(
  projectId: string,
): Promise<PriorPayment[]> {
  const snap = await getDocs(
    query(collection(db, 'payments'), where('projectId', '==', projectId)),
  );
  return snap.docs.map((d) => {
    const x = d.data();
    return {
      gateId: typeof x.gateId === 'string' ? x.gateId : '',
      amount: Number(x.amountINR) || 0,
      partial: x.partial === true,
    };
  });
}

/** Per-project foreign-settlement rollup (USD received + INR banked). */
export interface SettledTotals {
  /** Σ amount received in USD across the project's USD-received payments. */
  receivedUSD: number;
  /** Σ INR the bank actually credited for those USD receipts. */
  settledINR: number;
  /** How many USD-received payment rows carried a bank-credit figure. */
  count: number;
}

/**
 * Read a project's `payments/` ledger and roll up the FOREIGN-settlement
 * figures (USD received vs INR credited). Only rows recorded as a USD receipt
 * (`currencyReceived === 'USD'`) with an `amountSettledINR` contribute — this
 * is the "INR actually banked" picture for the Payments overview. INR receipts
 * carry no settlement (received == settled) and are ignored here. PURE-of-gate:
 * this NEVER feeds the gate or the USD receivable; it is a banking fact.
 */
export async function loadSettledTotals(
  projectId: string,
): Promise<SettledTotals> {
  const snap = await getDocs(
    query(collection(db, 'payments'), where('projectId', '==', projectId)),
  );
  let receivedUSD = 0;
  let settledINR = 0;
  let count = 0;
  for (const d of snap.docs) {
    const x = d.data();
    if (x.currencyReceived !== 'USD') continue;
    const banked = Number(x.amountSettledINR) || 0;
    const recv = Number(x.amountReceivedFx ?? x.amountINR) || 0;
    if (banked > 0) {
      settledINR += banked;
      receivedUSD += recv;
      count += 1;
    }
  }
  return { receivedUSD, settledINR, count };
}

/**
 * Read the ACTIVE override reminders for a project → `{ gateId: outstanding }`.
 * A gate released-unpaid via admin override seeds a `payment_reminders` doc
 * (status `armed_pending` | `active`) chasing the balance — this is the
 * authoritative "override-open / chasing" signal `payableGates` consumes.
 * A stopped/paid reminder is excluded. Team-readable per firestore.rules.
 * Also returns the reminder doc id per gate so a settling payment can stop it.
 */
export async function loadChasingByGate(
  projectId: string,
): Promise<{
  chasingByGate: Record<string, number>;
  reminderIdByGate: Record<string, string>;
}> {
  const snap = await getDocs(
    query(
      collection(db, 'payment_reminders'),
      where('projectId', '==', projectId),
    ),
  );
  const chasingByGate: Record<string, number> = {};
  const reminderIdByGate: Record<string, string> = {};
  for (const d of snap.docs) {
    const x = d.data();
    const status = String(x.status || '');
    if (status !== 'armed_pending' && status !== 'active') continue;
    const gateId = typeof x.gateId === 'string' ? x.gateId : '';
    if (!gateId) continue;
    const outstanding = Number(x.outstandingINR) || 0;
    if (outstanding > 0) {
      chasingByGate[gateId] = outstanding;
      reminderIdByGate[gateId] = d.id;
    }
  }
  return { chasingByGate, reminderIdByGate };
}

export interface SubmitInput {
  project: PayContextProject;
  gate: PayableGate;
  /** Amount in the GATE/project currency (= amount received in `currencyReceived`). */
  amount: number;
  mode: 'full' | 'partial';
  uiMethod: UiMethod;
  reference: string;
  actorUid: string;
  actorName: string;
  /**
   * Currency the client actually PAID in (default = project currency). When
   * 'USD' on a USD gate, `settledINR` carries the INR the bank credited.
   */
  currencyReceived?: Currency;
  /**
   * INR amount the bank actually CREDITED after forex (USD receipts only).
   * Drives the derived effective rate. Omit / null for an INR receipt, or for
   * a USD receipt whose bank credit has not yet landed.
   */
  settledINR?: number | null;
  /**
   * The active override reminder doc id on this gate, when settling an
   * override (from `loadChasingByGate`). Passed to `markPaidManual` so a
   * clearing settlement stops the chase. Omit for a normal gate.
   */
  reminderId?: string;
}

export interface SubmitResult {
  plan: PaymentPlan;
  /** true → the gate was cleared (via markPaidManual). */
  cleared: boolean;
  /** payments/ doc id (CF-written when cleared, or the partial ledger doc). */
  partialPaymentId: string | null;
  /** The settlement record persisted with the payment (USD ↔ INR), if any. */
  settlement: SettlementFx;
}

/**
 * The single record-payment write path. Re-runs the PURE `planPayment` (the
 * UI showed it, but we re-derive server-of-the-client to be safe), then:
 *   • clearsGate → `markPaidManual` (the CF does the atomic clear + ledger +
 *     reminder-stop). For a settlement this also stops the chase.
 *   • !clearsGate → a ledger-only partial `payments/` doc (gate stays open).
 *
 * MULTI-CURRENCY: `amount` is in the GATE currency (USD for a USD gate) and
 * is what clears/partials the gate — NEVER FX-converted. When the client paid
 * USD, the INR the bank credited (`settledINR`) + the derived effective rate
 * are persisted ALONGSIDE on the `payments/` doc (banking fact, not gate math):
 *   • partial path  — they ride on the client-SDK create directly.
 *   • clearing path — the deployed CF doesn't persist them yet, so we stamp
 *     them onto the CF-written doc via a follow-up `updateDoc` (transitional
 *     bridge; the prepared CF patch makes the CF author them natively, after
 *     which this follow-up is a harmless no-op overwrite of identical values).
 *     The follow-up writes ONLY settlement metadata — never amount/gate fields
 *     — so it can never affect the gate or the running balance.
 * Throws on an invalid plan.
 */
export async function submitPayment(input: SubmitInput): Promise<SubmitResult> {
  const { project, gate, amount, mode, uiMethod, reference } = input;
  const plan = planPayment({ gate, amount, mode });
  if (plan.error) throw new Error(plan.error);

  // Build the settlement record. Received currency defaults to the gate
  // currency; an INR receipt produces an empty (no-FX) settlement.
  const currencyReceived: Currency = input.currencyReceived ?? project.currency;
  const settlement = settlementOf(
    currencyReceived,
    amount,
    input.settledINR ?? null,
  );

  if (plan.clearsGate) {
    const call = buildMarkPaidCall(
      project,
      gate,
      amount,
      uiMethod,
      reference,
      settlement,
    );
    // A settling payment carries the reminder id so the CF stops the chase;
    // for a normal gate the CF stops any running reminder on the project.
    const res = await markPaidManual(
      input.reminderId ? { ...call, reminderId: input.reminderId } : call,
    );
    // Bridge: stamp the FX/settlement fields onto the CF-written doc until the
    // CF patch persists them natively. USD receipts only (INR → empty → skip).
    const fx = settlementFields(settlement);
    if (res.paymentId && Object.keys(fx).length > 0) {
      try {
        await updateDoc(doc(db, 'payments', res.paymentId), {
          ...fx,
          settlementStampedBy: input.actorName,
          settlementStampedAt: serverTimestamp(),
        });
      } catch {
        // Non-fatal: the gate is already cleared + the payment recorded. The
        // FX trail can be re-stamped; never fail a recorded payment over it.
      }
    }
    return {
      plan,
      cleared: true,
      partialPaymentId: res.paymentId ?? null,
      settlement,
    };
  }

  // Partial (or partial settlement) — ledger-only, gate stays open. The
  // tighter payments/ rule permits an isTeam() create with partial:true.
  const ref = await addDoc(collection(db, 'payments'), {
    ...partialPaymentBody(
      project,
      gate,
      amount,
      uiMethod,
      reference,
      input.actorUid,
      input.actorName,
      settlement,
    ),
    at: serverTimestamp(),
  });
  return { plan, cleared: false, partialPaymentId: ref.id, settlement };
}

/* ════════════════════ Stamp per-project money fields ════════════════════ */

export interface MoneyStampPatch {
  /** Billing currency — drives firm auto-route + method set. */
  currency?: Currency;
  /** Engagement value (project currency) — drives %-split gate amounts. */
  valueINR?: number;
  /** Explicit firm override; omit to auto-route from currency+gst. */
  firmId?: FirmId | null;
  /** GST-billed? Defaults INR→true, USD→false when omitted. */
  gst?: boolean;
}

/**
 * Stamp `currency` / `firmId` / `gst` / `valueINR` onto a project doc so the
 * money module's defensive defaults become real per-project values (this is
 * what makes the Payments figures exact). `firmId` is written explicitly when
 * given, else AUTO-derived from `firmFor({currency, gst})` and persisted so
 * the routing is stable. Only the bounded money fields are written —
 * `gateState` is untouched (the rules' `gateStateUnchanged()` guard is not
 * tripped; these are ordinary project fields).
 */
export async function stampProjectMoney(
  pid: string,
  patch: MoneyStampPatch,
  actorName: string,
): Promise<void> {
  const out: Record<string, unknown> = {};
  if (patch.currency === 'INR' || patch.currency === 'USD') {
    out.currency = patch.currency;
  }
  if (typeof patch.valueINR === 'number' && patch.valueINR >= 0) {
    out.valueINR = Math.round(patch.valueINR);
  }
  if (typeof patch.gst === 'boolean') out.gst = patch.gst;

  // Resolve the firm: explicit override wins; else auto-route from the
  // resolved currency/gst so a stamped project always carries a real firmId.
  const currency: Currency =
    patch.currency === 'USD' ? 'USD' : patch.currency === 'INR' ? 'INR' : 'INR';
  const gst =
    typeof patch.gst === 'boolean' ? patch.gst : currency === 'USD' ? false : true;
  if (patch.firmId === 'firm1' || patch.firmId === 'firm2') {
    out.firmId = patch.firmId;
  } else if (patch.currency || typeof patch.gst === 'boolean') {
    out.firmId = firmFor({ currency, gst });
  }

  if (Object.keys(out).length === 0) return;
  // CLEAN MANUAL PAYMENTS (2026-06-13) — pricing an engagement IS the act of
  // tracking it in Payments. So a successful stamp also flips
  // `trackedInPayments:true`, and the engagement appears in the Payments
  // overview / ₹-$ split / firm rollup. (Untracked legacy/test projects stay
  // out of the money picture — they are never deleted, just not surfaced.)
  out.trackedInPayments = true;
  out.updatedBy = actorName;
  out.updatedAt = serverTimestamp();
  await updateDoc(doc(db, 'projects', pid), out);
}

/**
 * AGREED PRICE on a LIVE project (2026-06-15 — the $40 fix).
 *
 * Sets the agreed currency + amount on an already-converted project (editable
 * any time, even after work has started — owner directive). Writes the bounded
 * money fields (`currency` / `valueINR` / `firmId` / `gst` / `gateAmounts`) so
 * the money gate expects the AGREED figure in the AGREED currency — closing the
 * "$11,000" bug (a USD $40 engagement now expects $40, not the ₹11,000 template
 * number wearing a "$"). Appends a change-history row (was→now/by/when) via
 * `arrayUnion` so money never changes silently (MOCKUP_3). Pricing the
 * engagement IS tracking it in Payments (mirrors `stampProjectMoney`).
 *
 * Only ordinary project fields are written — `gateState` is UNTOUCHED, so the
 * rules' `gateStateUnchanged()` guard is not tripped and the existing
 * `isTeam()` update permits it (no firestore.rules change).
 */
export async function setProjectAgreedPrice(
  pid: string,
  input: {
    tier: Tier | string;
    currency: Currency;
    value: number;
    /** Prior agreed state — drives the history was→now. */
    prior: { currency: Currency | null; value: number | null };
    gst?: boolean;
    note?: string;
  },
  actorName: string,
): Promise<{ historyEntry: FeeHistoryEntry | null }> {
  const update = buildAgreedPriceUpdate({
    tier: input.tier,
    currency: input.currency,
    value: input.value,
    prior: input.prior,
    gst: input.gst,
    note: input.note,
    actorName,
    now: new Date().toISOString(),
  });
  // NO-OP GUARD (Bug B) — a same-value/same-currency edit (e.g. "was $40 → now
  // $40") writes NO change-history row. The fields are already correct, so the
  // patch is a no-op too; skip the whole write to avoid a meaningless update.
  if (update.noop) {
    return { historyEntry: null };
  }
  await updateDoc(doc(db, 'projects', pid), {
    ...update.patch,
    feeHistory: arrayUnion(update.historyEntry),
    trackedInPayments: true,
    updatedBy: actorName,
    updatedAt: serverTimestamp(),
  });
  return { historyEntry: update.historyEntry as FeeHistoryEntry };
}

/**
 * CLEAN MANUAL PAYMENTS (2026-06-13, owner directive) — add or remove a
 * project from the Payments view WITHOUT changing its money. Setting
 * `trackedInPayments:true` surfaces the engagement in the Payments overview;
 * `false` removes it (the project + its data are untouched — it simply stops
 * appearing as money). `trackedInPayments` is an ordinary project field
 * (NOT gateState), so the rules' `gateStateUnchanged()` guard is not tripped
 * and `isTeam()` update already permits it — no firestore.rules change.
 */
export async function setProjectTrackedInPayments(
  pid: string,
  tracked: boolean,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'projects', pid), {
    trackedInPayments: tracked === true,
    updatedBy: actorName,
    updatedAt: serverTimestamp(),
  });
}
